import React, { useCallback, useMemo } from "react";
import {
  AHIcon,
  AHNumberInput,
  Box,
  resolveAHTextStyle,
  useLayoutContext,
  useScaleFactor,
  useTheme,
} from "arkh";
import { graphql, useFragment, useMutation } from "react-relay";
import { useDebouncedMutation } from "../../../../common/Utils/useDebouncedMutation";

import { TodoEditNewMainFooterResponseNumber_todo$key } from "./__generated__/TodoEditNewMainFooterResponseNumber_todo.graphql";
import { TodoEditNewMainFooterResponseNumberMutation } from "./__generated__/TodoEditNewMainFooterResponseNumberMutation.graphql";
import { useIntl } from "react-intl";
import { View } from "react-native";

export function TodoEditNewMainFooterResponseNumber(props: {
  todo: TodoEditNewMainFooterResponseNumber_todo$key;
  autofocus?: boolean;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoEditNewMainFooterResponseNumber_todo on Todo {
        id
        canWrite
        isResponseCompleted
        status
        response {
          ... on TodoResponseNumber {
            value
          }
        }
      }
    `,
    props.todo
  );

  const [mutation] = useMutation<TodoEditNewMainFooterResponseNumberMutation>(
    graphql`
      mutation TodoEditNewMainFooterResponseNumberMutation(
        $todoId: ID!
        $input: UpdateTodoNumberResponseInput!
      ) {
        todo(todoId: $todoId) {
          updateNumberResponse(input: $input) {
            isResponseCompleted
            canChangeStatus
            parent {
              canChangeStatus
            }
            response {
              ... on TodoResponseNumber {
                value
              }
            }
          }
        }
      }
    `
  );

  const remoteValue = todo.response?.value ?? undefined;
  const setValue = useCallback(
    (value: number | undefined) => {
      mutation({
        variables: {
          todoId: todo.id,
          input: {
            number: value,
          },
        },
      });
    },
    [mutation, todo.id]
  );
  const [local, setR] = useDebouncedMutation(remoteValue, setValue);
  const intl = useIntl();
  const layout = useLayoutContext();
  const sf = useScaleFactor();
  const theme = useTheme();
  const subtitleTextStyle = useMemo(
    () => resolveAHTextStyle("subtitle", sf, theme, layout),
    [sf, theme, layout]
  );
  const disabled = todo.status === "DONE" || !todo.canWrite;
  return (
    <View pointerEvents={disabled ? "box-none" : "auto"}>
      <AHNumberInput
        hitSlop={{ top: 10, bottom: 10 }}
        leftElement={
          <Box
            style={{
              width: layout.space.medium + layout.circle.smallMedium,
              flexDirection: "row",
              justifyContent: "flex-end",
            }}
          >
            <AHIcon
              type="number-input"
              color="primary"
              forceSize={layout.circle.smallMedium}
            />
          </Box>
        }
        buttonStyle={{
          backgroundColor: theme.background,
          opacity: 1,
        }}
        buttonsShow={true}
        autoFocus={props.autofocus && !todo.isResponseCompleted}
        inputContainerStyle={{
          width: null,
          flex: 1,
          justifyContent: "flex-start",
          paddingLeft: 0,
          marginLeft: layout.space.small,
          marginRight: layout.space.small,
        }}
        textInputStyle={{
          textAlign: "left",
          paddingLeft: 0,
          ...subtitleTextStyle,
        }}
        space="xsmall"
        cornerRadius="medium"
        value={local}
        setValue={setR}
        placeholder={intl.formatMessage({
          defaultMessage: "Enter number...",
          description: "Todo number default response placeholder",
        })}
        clearTextOnFocus={false}
        disabled={disabled}
        maxValue={10000000000}
        minValue={0}
      />
    </View>
  );
}
